/**
 * Standardized query key factory for procurement module
 * Provides consistent caching and invalidation patterns
 */

export const procurementQueryKeys = {
  // Base namespace
  all: ['procurement'] as const,
  
  // Project-specific queries
  project: (projectId: string) => [...procurementQueryKeys.all, 'project', projectId] as const,
  
  // Data queries
  data: (projectId: string, filters?: Record<string, any>) => 
    [...procurementQueryKeys.project(projectId), 'data', filters] as const,
  
  // Orders queries
  orders: (projectId: string, filters?: Record<string, any>) => 
    [...procurementQueryKeys.project(projectId), 'orders', filters] as const,
    
  // Contractors queries
  contractors: (projectId: string, filters?: Record<string, any>) => 
    [...procurementQueryKeys.project(projectId), 'contractors', filters] as const,
    
  // Payment queries
  payments: (projectId: string, filters?: Record<string, any>) => 
    [...procurementQueryKeys.project(projectId), 'payments', filters] as const,
    
  // Payment milestones queries (replacing legacy 'payment_milestones' keys)
  paymentMilestones: (projectId: string, startDate?: string, endDate?: string) => 
    [...procurementQueryKeys.project(projectId), 'payment-milestones', { startDate, endDate }] as const,
    
  // Summary queries
  summary: (projectId: string, startDate?: string, endDate?: string) => 
    [...procurementQueryKeys.project(projectId), 'summary', { startDate, endDate }] as const,
} as const;

export const globalProcurementQueryKeys = {
  // Base namespace
  all: ['global-procurement'] as const,
  
  // Multi-project orders queries
  orders: (projectIds: string[], filters?: Record<string, any>, cursor?: string | null) => 
    [...globalProcurementQueryKeys.all, 'orders', projectIds, filters, cursor] as const,
    
  // Multi-project payment summary queries
  paymentSummary: (projectIds: string[], filters?: Record<string, any>, startDate?: string, endDate?: string) => 
    [...globalProcurementQueryKeys.all, 'payment-summary', projectIds, filters, { startDate, endDate }] as const,
} as const;

export const subconsQueryKeys = {
  // Base namespace
  all: ['subcons'] as const,
  
  // List queries
  list: (filters?: Record<string, any>) => 
    [...subconsQueryKeys.all, 'list', filters] as const,
    
  // Detail queries
  detail: (id: string) => 
    [...subconsQueryKeys.all, 'detail', id] as const,
} as const;

// Helper function for query key generation
export const createQueryKey = (
  namespace: string, 
  operation: string, 
  params?: Record<string, any>
): string[] => {
  const key = [namespace, operation];
  if (params) {
    key.push(JSON.stringify(params));
  }
  return key;
};